import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import toast from "react-hot-toast";
import { Link } from "react-router";

import { UserMinusIcon, UserPlusIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useThrottle } from "simplydo/react/hooks";
import { Button } from "simplydo/react/ui/button";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Pagination } from "simplydo/react/ui/pagination";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChooser } from "components/lib/Choosers";
import ConfigurableTable from "components/lib/ConfigurableTable";

import type { RoleUsersPage, RoleWithInformation, RolesUser } from "../types";

type RoleEditorAssignmentProps = {
  forType: OpenAPI.Schemas["Role"]["forType"];
  forId: string;
  usingRole: RoleWithInformation;
  setRoles: Dispatch<SetStateAction<RoleWithInformation[]>>;
};

const RoleEditorAssignment = ({
  forType,
  forId,
  setRoles,
  usingRole,
}: RoleEditorAssignmentProps) => {
  const [usersSearchState, setUsersSearchState] = useState<{
    loading: boolean;
    query: string;
    page: number;
  }>({ loading: false, query: "", page: 1 });
  const [usersState, setUsersState] = useState<RoleUsersPage>({
    users: [],
    total: 0,
    nextPageAvailable: false,
    prevPageAvailable: false,
  });
  const [assigningUsers, setAssigningUsers] = useState(false);

  const user: RolesUser = useCurrentUser();
  const canViewDashboard = util.hasPermission(
    user,
    "org.viewDashboard",
    user?.ownerOrganisation?._id ?? "",
  );
  const canListUsers = util.hasPermission(
    user,
    "org.listUsers",
    user?.ownerOrganisation?._id ?? "",
  );

  const usingForId = forType === "global" ? "global" : forId;

  const fetchAssignedUsers = useThrottle(
    (query: string, page: number) => {
      if (!forId || !forType) return;

      api.roles.getAssignedRoleUsers(
        forType,
        forId,
        usingRole._id,
        { page, ...(query ? { query } : {}) },
        (data) => {
          setUsersSearchState((prevSearchState) => ({
            ...prevSearchState,
            loading: false,
          }));
          setUsersState((prevState) => ({
            ...prevState,
            users: data.users,
            total: data.total,
            nextPageAvailable: data.nextPageAvailable,
            prevPageAvailable: data.prevPageAvailable,
          }));
        },
        (err) => {
          setUsersSearchState((prevSearchState) => ({
            ...prevSearchState,
            loading: false,
          }));
          toast.error(err.message);
        },
      );
    },
    250,
    [forId, forType, usingRole._id],
  );

  const getAssignedUsers = useCallback(
    (query: string, page: number) => {
      setUsersSearchState((prevSearchState) => ({
        ...prevSearchState,
        loading: true,
        query,
        page,
      }));
      fetchAssignedUsers(query, page);
    },
    [fetchAssignedUsers],
  );

  useEffect(() => {
    getAssignedUsers("", 1);
  }, [getAssignedUsers]);

  const assignRoleToUsers = useCallback(
    (userIds: string[]) => {
      if (userIds.length === 0) return;

      setAssigningUsers(true);
      api.roles.assignRole(
        forType,
        usingForId,
        usingRole._id,
        userIds,
        () => {
          setAssigningUsers(false);
          toast.success(
            userIds.length === 1
              ? `Role ${usingRole.name} assigned to user`
              : `Role ${usingRole.name} assigned to ${userIds.length} users`,
          );
          getAssignedUsers(usersSearchState.query, usersSearchState.page);
          setRoles((prevRoles) =>
            prevRoles.map((role) => {
              if (role._id === usingRole._id) {
                return {
                  ...role,
                  userCount: (role.userCount ?? 0) + userIds.length,
                };
              }
              return role;
            }),
          );
        },
        (err) => {
          setAssigningUsers(false);
          toast.error(err.message);
        },
      );
    },
    [
      forType,
      setRoles,
      usingForId,
      usingRole._id,
      usingRole.name,
      usersSearchState,
      getAssignedUsers,
    ],
  );

  const unassignRoleFromUser = useCallback(
    (userId: string) => {
      api.roles.unassignRole(
        forType,
        usingForId,
        usingRole._id,
        [userId],
        () => {
          toast.success(`Role ${usingRole?.name} unassigned from user`);
          setUsersState((prevUsersState) => ({
            ...prevUsersState,
            users: prevUsersState.users.filter(
              (prevUser) => prevUser._id !== userId,
            ),
            total: Math.max(0, prevUsersState.total - 1),
          }));
          setRoles((prevRoles) =>
            prevRoles.map((role) => {
              if (role._id === usingRole._id) {
                return { ...role, userCount: (role.userCount ?? 0) - 1 };
              }
              return role;
            }),
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [forType, setRoles, usingForId, usingRole],
  );

  const handleUnassignUser = useCallback(
    (selectedUser: OpenAPI.Schemas["users"]) => {
      if (user?._id === selectedUser._id) {
        util
          .confirm(
            "Unassign yourself",
            "Are you sure you want to unassign yourself from this role? This might result in you losing access to this resource.",
          )
          .then(() => {
            unassignRoleFromUser(selectedUser._id);
          })
          .catch(() => {});
        return;
      }
      unassignRoleFromUser(selectedUser._id);
    },
    [unassignRoleFromUser, user?._id],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          gap: 10,
        }}
      >
        <H5 style={{ margin: 0 }}>Currently assigned users</H5>
        <UserChooser
          trigger={
            <Button color="primary">
              <Icon icon={UserPlusIcon} />
              Assign users
            </Button>
          }
          enabledFeatures={{ search: true }}
          confirm="Assign users"
          clearOnComplete
          isLoading={assigningUsers}
          searchFunction={(term, cb, failure) =>
            api.roles.getPotentialRoleUsers(
              forType,
              forId,
              usingRole._id,
              { ...(term ? { query: term } : {}) },
              (res) =>
                cb(
                  (res.users as OpenAPI.Schemas["User"][]).filter(
                    (potentialUser) =>
                      !(potentialUser.roleIds ?? []).includes(usingRole._id),
                  ),
                ),
              failure,
            )
          }
          onComplete={(users) => {
            const userIds = users
              .filter((u) => !u.isEmailInvitee)
              .map((u) => u._id);
            assignRoleToUsers(userIds);
          }}
        />
      </div>
      {usersState?.users?.length > 0 ? (
        <ConfigurableTable
          tableKey="roleEditorAssignment"
          data={usersState.users}
          keyExtractor={(tableUser) => tableUser._id}
          actions={[
            {
              name: "Unassign",
              icon: UserMinusIcon,
              appear: "single",
              onClick: (items) => {
                const selectedUser = items[0];
                if (!selectedUser) return;
                handleUnassignUser(selectedUser);
              },
            },
          ]}
          columns={[
            {
              key: "image",
              name: "",
              settingName: "Image",
              width: 40,
              center: true,
              render: ({ item }) => (
                <Link to={`/users/${item._id}`}>
                  <ImageWithFallback
                    style={{ objectFit: "cover" }}
                    avatar
                    src={util.avatarUrl(item)}
                    fallbackSrc={util.avatarUrl()}
                  />
                </Link>
              ),
            },
            {
              key: "firstName",
              name: "First name",
              render: ({ item }) => (
                <Link to={`/users/${item._id}`}>{item.profile.firstName}</Link>
              ),
            },
            {
              key: "lastName",
              name: "Last name",
              render: ({ item }) => (
                <Link to={`/users/${item._id}`}>{item.profile.lastName}</Link>
              ),
            },
            {
              key: "email",
              name: "Email",
              render: ({ item }) => item.emails?.[0]?.address ?? "",
              isVisible: canViewDashboard && canListUsers,
            },
            {
              key: "department",
              name: "Department",
              render: ({ item }) => item.profile.department,
            },
          ]}
        />
      ) : (
        <p style={{ marginTop: 0, opacity: 0.7 }}>
          No users are currently assigned to this role.
        </p>
      )}
      {usersState.prevPageAvailable || usersState.nextPageAvailable ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            activePage={usersSearchState.page}
            totalPages={Math.ceil(usersState.total / 10)}
            onPageChange={(_, { activePage }) => {
              getAssignedUsers(usersSearchState.query, activePage as number);
            }}
            ellipsisItem={null}
          />
        </div>
      ) : null}
    </div>
  );
};

export default RoleEditorAssignment;
